import { useEffect, useState } from "react";
import { PageContainer } from "@greeter/matter";
import {
  IonBackButton,
  IonContent,
  IonLabel,
  IonModal,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonToolbar,
  isPlatform,
  useIonViewWillEnter,
} from "@ionic/react";
import { Card, ProgressBar } from "@greeter/matter-v2";
import {
  IconCheck,
  IconChecks,
  IconChevronRight,
  IconCircleCheck,
  IconCircleDashed,
  IconCircleXFilled,
  IconSearch,
} from "@tabler/icons-react";
import {
  Ticket,
  TicketEvent,
  TicketId,
  TicketOrder,
  TicketOrderId,
} from "@greeter/core";
import { useActor, useSelector } from "@xstate/react";
import {
  TicketOverviewApiRequirements,
  TicketOverviewMachine,
} from "./TicketOverviewMachine";
import { useFetchActorLoading } from "@greeter/xstate-util";
import { classNames, isType } from "@greeter/util";
import { addSeconds, addYears, formatDistanceToNow, subYears } from "date-fns";
import { da as danishLocale } from "date-fns/locale";
import { dropInOutFromBottom, staggerForwardsBackwards } from "@greeter/motion";
import { AnimatePresence, HTMLMotionProps, motion } from "motion/react";
import { NotFoundView } from "./components";

import css from "./TicketOverviewPage.module.scss";
import { DatePeriod, TimeOfDayPeriod, withTimeOfDay } from "@greeter/date";
import { TicketEventCard } from "./components/TicketEventCard/TicketEventCard";
import { SpinnerView } from "./components/SpinnerView/SpinnerView";

function range(size: number) {
  const a: number[] = [];
  for (let i = 0; i < size; i++) {
    a.push(i + 1);
  }
  return a;
}

export function SearchInput({
  className,
  ...props
}: React.ComponentProps<"input"> & { onClear?: () => void }) {
  return (
    <div className={css.SearchInputContainer}>
      <IconSearch className={css.SearchIcon} />
      <input {...classNames(css.SearchInput, className)} {...props} />
      {(props.value as string)?.toString().length > 0 && (
        <IconCircleXFilled className={css.ClearIcon} onClick={props.onClear} />
      )}
    </div>
  );
}

export function TicketOrderCard(props: {
  ticketOrder: TicketOrder;
  tickets?: Ticket[];
  ticketEvent?: TicketEvent;
  focus?: boolean;
  fade?: boolean;
  onClick?: () => void;
  onBlur?: () => void;

  onCheckInNext?: () => void;
  onCheckInAll?: () => void;

  loading?: {
    checkingIn?: Set<TicketId>;
    checkingAllIn?: boolean;
    fetchingTickets?: boolean;
  };
}) {
  return (
    <Card
      layout
      variant="dark"
      onClick={props.onClick}
      onBlur={props.onBlur}
      act="button"
    >
      <Card.Content
        {...classNames(
          css.TicketOrderCardContent,
          props.focus && css.Open,
          props.focus ? "flex-column" : "flex-row"
        )}
        style={{
          position: "relative",
        }}
      >
        <motion.div
          layout="position"
          className={css.TicketOrderCardInnerContent}
        >
          <TicketOrderCard.CustomerName
            style={{
              width: "fit-content",
              left: props.focus ? "50%" : 0,
              position: "relative",
              x: props.focus ? "-50%" : 0,
            }}
          >
            {props.ticketOrder.customerName}
          </TicketOrderCard.CustomerName>
          <AnimatePresence mode="popLayout">
            {!props.focus && (
              <TicketOrderCard.ScanProgressSummary
                total={props.ticketOrder.ticketCount}
                current={props.ticketOrder.ticketCheckedInCount}
                {...dropInOutFromBottom}
              />
            )}
            {props.focus &&
              props.ticketEvent &&
              isType(props.ticketEvent.eventPeriod, "once") && (
                <TicketOrderCard.Date
                  key="date"
                  date={new Date(props.ticketEvent.eventPeriod.date)}
                />
              )}
          </AnimatePresence>
          <TicketOrderCard.TicketEventName
            style={{
              textAlign: props.focus ? "center" : "left",
              color: "#6B7280",
              fontSize: 12,
            }}
          >
            {props.ticketOrder.ticketEventTitle}
          </TicketOrderCard.TicketEventName>
        </motion.div>
        {!props.focus && (
          <div className={css.Icons}>
            {props.ticketOrder.ticketCheckedInCount >=
            props.ticketOrder.ticketCount ? (
              <div className={css.IconCheck}>
                <IconCheck style={{ width: 20 }} />
              </div>
            ) : (
              <IconChevronRight />
            )}
          </div>
        )}
      </Card.Content>
      <AnimatePresence mode="popLayout">
        {props.focus && (
          <Card.Content
            layout="position"
            {...dropInOutFromBottom}
            className={css.ScanProgressContainer}
          >
            <TicketOrderCard.ScanProgress
              current={props.ticketOrder.ticketCheckedInCount}
              total={props.ticketOrder.ticketCount}
              tickets={props.tickets ?? []}
              loading={props.loading?.checkingIn ?? new Set()}
              loadingAll={
                props.loading?.fetchingTickets || props.loading?.checkingAllIn
              }
            />
          </Card.Content>
        )}
        {props.focus && props.tickets?.some((t) => !t.checkedInAt) && (
          <Card.Content
            {...dropInOutFromBottom}
            layout="position"
            className={css.ButtonBar}
          >
            <Card
              {...dropInOutFromBottom}
              layout="position"
              variant="green"
              className={css.Button}
              act="button"
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                props.onCheckInAll?.();
              }}
              disabled={
                (props.loading?.checkingIn?.size ?? 0) > 0 ||
                props.loading?.checkingAllIn
              }
            >
              <Card.Content padding="sm" className={css.ButtonContent}>
                {props.loading?.checkingAllIn ? (
                  <IonSpinner style={{ height: 20 }} />
                ) : (
                  <IconChecks style={{ height: 20 }} />
                )}
                <span>Tjek alle ind</span>
              </Card.Content>
            </Card>

            <Card
              {...dropInOutFromBottom}
              layout="position"
              variant="green"
              className={css.Button}
              act="button"
              onClick={(ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                props.onCheckInNext?.();
              }}
              disabled={
                !!props.loading?.checkingIn || props.loading?.checkingAllIn
              }
            >
              <Card.Content padding="sm" className={css.ButtonContent}>
                {props.loading?.checkingIn ? (
                  <IonSpinner style={{ height: 20 }} />
                ) : (
                  <IconCheck style={{ height: 20 }} />
                )}
                <span>Tjek 1 ind</span>
              </Card.Content>
            </Card>
          </Card.Content>
        )}
      </AnimatePresence>
    </Card>
  );
}

TicketOrderCard.TicketItem = function (
  props: {
    scannedAt?: Date;
    index: number;
    loading?: boolean;
  } & HTMLMotionProps<"div">
) {
  function justScanned() {
    const now = new Date();
    return props.scannedAt && now < addSeconds(props.scannedAt, 10);
  }

  return (
    <motion.div
      style={{
        display: "grid",
        gridTemplateColumns: "18px 1fr auto",
        gap: 5,
        alignItems: "center",
        textAlign: "center",
      }}
      {...props}
    >
      {props.loading && <IonSpinner style={{ width: 18, height: 18 }} />}
      {!props.loading && props.scannedAt && (
        <IconCircleCheck style={{ color: "#2ECC71", width: 18, height: 18 }} />
      )}
      {!props.loading && !props.scannedAt && (
        <IconCircleDashed style={{ color: "#4B5563", width: 18, height: 18 }} />
      )}
      <span
        style={{
          color: justScanned()
            ? "white"
            : !justScanned() && props.scannedAt
            ? "#D1D5DB"
            : "#9CA3AF",
          fontSize: 14,
          textAlign: "left",
        }}
      >
        Billet {props.index}
      </span>
      {justScanned() && (
        <span
          style={{
            fontSize: 10,
            color: "#2ECC71",
            backgroundColor: "#14532D4D",
            padding: "2px 8px",
            borderRadius: "100vw",
            fontWeight: "bold",
          }}
        >
          Netop nu
        </span>
      )}
      {!justScanned() && props.scannedAt && (
        <span
          style={{
            fontSize: 10,
            color: "#6B7280",
          }}
        >
          {formatDistanceToNow(props.scannedAt, { locale: danishLocale })}
        </span>
      )}
      {!props.scannedAt && (
        <span
          style={{
            fontSize: 10,
            color: "#4B5563",
          }}
        >
          AFVENTER
        </span>
      )}
    </motion.div>
  );
};

TicketOrderCard.ScanProgressSummary = function (
  props: {
    total: number;
    current: number;
  } & HTMLMotionProps<"span">
) {
  return (
    <motion.span
      style={{
        color: "#FFFFFF",
        fontSize: 12,
        textAlign: "left",
      }}
      {...props}
    >
      {props.current} ud af {props.total} billetter scannet
    </motion.span>
  );
};
export const dateFormatter = new Intl.DateTimeFormat("da-DK", {
  month: "short",
  day: "numeric",
  year: "numeric",
});
TicketOrderCard.Date = function ({
  style,
  ...props
}: {
  date: Date;
} & HTMLMotionProps<"span">) {
  return (
    <motion.span
      style={{
        textAlign: "center",
        color: "#6B7280",
        fontSize: 12,
        transition: "200ms",
        ...style,
      }}
      {...props}
    >
      {dateFormatter.format(props.date)}
    </motion.span>
  );
};
TicketOrderCard.ScanProgress = function (props: {
  total: number;
  current: number;
  tickets: Array<Ticket>;
  loading: Set<TicketId>;
  loadingAll?: boolean;
}) {
  return (
    <Card
      {...dropInOutFromBottom}
      variant="dark-blue"
      style={{
        width: "100%",
        height: "100%",
      }}
    >
      <Card.Content>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            paddingBottom: 10,
          }}
        >
          <span style={{ fontSize: 12, color: "#9CA3AF" }}>BILLETOVERSIGT</span>
          <span style={{ fontSize: 12, color: "white" }}>
            {props.current} af {props.total}
          </span>
        </div>
        <ProgressBar value={props.current} target={props.total} />

        <motion.div {...dropInOutFromBottom} layout className={css.TicketItems}>
          <AnimatePresence mode="popLayout">
            {range(props.total).map((n, i) => {
              const ticket = props.tickets?.[i];
              return (
                <TicketOrderCard.TicketItem
                  layout="position"
                  {...staggerForwardsBackwards(
                    dropInOutFromBottom,
                    0.05,
                    i,
                    props.total
                  )}
                  key={n}
                  index={n}
                  scannedAt={ticket?.checkedInAt ?? undefined}
                  loading={props.loadingAll || props.loading.has(ticket?.id)}
                />
              );
            })}
          </AnimatePresence>
        </motion.div>
      </Card.Content>
    </Card>
  );
};
TicketOrderCard.CustomerName = function ({
  style,
  ...props
}: HTMLMotionProps<"span">) {
  return (
    <motion.span
      style={{ fontSize: 18, fontWeight: "bold", textAlign: "left", ...style }}
      {...props}
    />
  );
};
TicketOrderCard.Details = function () {};
TicketOrderCard.TicketEventName = function (props: HTMLMotionProps<"span">) {
  return (
    <motion.span
      layout="position"
      style={{
        textAlign: "left",
        color: "#6B7280",
        fontSize: 12,
      }}
      {...props}
    />
  );
};

export type TicketOverviewPageProps = {
  ticketEventId: string;
};

export function TicketOverviewPage(props: {
  api: TicketOverviewApiRequirements;
}) {
  const [snap, send, m] = useActor(TicketOverviewMachine, {
    input: { api: props.api },
  });
  const api = useSelector(m, (s) => s.context.api);
  const fetchTicketOrdersLoading = useFetchActorLoading(
    api.fetchTicketOrdersRef
  );
  const ticketOrders = useSelector(m, (s) =>
    Array.from(s.context.ticketOrders.values())
  );
  const tickets = useSelector(api.fetchTicketsRef, (s) => {
    return (
      s.context.data?.sort((a, b) => {
        return Number(!!b.checkedInAt) - Number(!!a.checkedInAt);
      }) ?? []
    );
  });
  const totalTickets =
    ticketOrders?.reduce((acc, curr) => acc + curr.ticketCount, 0) ?? 0;
  const totalCheckedIn =
    ticketOrders?.reduce((acc, curr) => acc + curr.ticketCheckedInCount, 0) ??
    0;

  const ticketEvent = useSelector(
    api.fetchTicketEventRef,
    (s) => s.context.data
  );

  const focused = useSelector(m, (s) => s.context.focusedTicketOrderId);

  useIonViewWillEnter(() => send({ type: "initialize" }), [send]);

  const checkingInTicket = useSelector(
    api.saveTicketRef,
    (s) => s.context.input?.[0]?.id
  );

  const saveTicketRefSnap = useSelector(api.saveTicketRef, (s) => s);

  const saveTicketsRefSnap = useSelector(api.saveTicketsRef, (s) => s);
  const fetchTicketsRefSnap = useSelector(api.fetchTicketsRef, (s) => s);
  const fetchTicketEventsSnap = useSelector(api.fetchTicketEventsRef, (s) => s);
  const isFetchTicketEventsLoading = useFetchActorLoading(
    api.fetchTicketEventsRef
  );

  const [pickingEvent, setPickingEvent] = useState(false);
  const [filter, setFilter] = useState<"future" | "previous">("future");

  useEffect(() => {
    fetchTicketEvents();
  }, [filter]);

  function fetchTicketEvents() {
    const period =
      filter === "future"
        ? new DatePeriod({
            from: new Date(),
            to: addYears(new Date(), 1),
          })
        : new DatePeriod({
            from: subYears(new Date(), 1),
            to: new Date(),
          });
    snap.context.api.fetchTicketEventsRef.send({
      type: "fetch",
      data: [
        {
          period: period,
        },
      ],
    });
  }

  function openEventPicker() {
    setPickingEvent(true);
    fetchTicketEvents();
  }

  return (
    <IonPage>
      <IonContent
        style={{
          "--background": "#0f1720",
        }}
      >
        <PageContainer className={css.Page}>
          {!fetchTicketOrdersLoading && !ticketOrders.length && (
            <div
              style={{
                background:
                  "radial-gradient(circle, rgb(34 197 94 / 50%) 0%, rgb(34 197 94 / 0%) 70%)",
                width: "200vh",
                height: "200vh",
                pointerEvents: "none",
                position: "absolute",
                top: "-125vh",
                right: "-125vh",
                zIndex: 0,
              }}
            />
          )}
          <div className={css.Header}>
            <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <h1 className={css.Title}>
                Gæsteliste (
                <span className={css.CheckedIn}>{totalCheckedIn ?? 0}</span>/
                {totalTickets ?? 0})
              </h1>
              <div className={css.SubTitleBar}>
                <span className={css.SubTitle}>{ticketEvent?.title}</span>
                <span
                  className={css.SubTitle}
                  style={{ textDecoration: "underline" }}
                  onClick={openEventPicker}
                >
                  Vælg andet event
                </span>
              </div>
            </div>
            <SearchInput
              placeholder="Søg efter navn, email eller ID"
              value={snap.context.search}
              onInput={(ev) =>
                send({ type: "set.search", data: ev.currentTarget.value })
              }
              onClear={() => send({ type: "set.search", data: "" })}
            />
          </div>
          <div className={css.Content}>
            {fetchTicketOrdersLoading && <span>Henter...</span>}
            {snap.context.search.length >= 2 && ticketOrders.length === 0 && (
              <span>
                Kunne ikke finde nogle billetter med søgningen{" "}
                <q>{snap.context.search}</q>
              </span>
            )}
            {ticketOrders
              ?.filter(
                (to) => to.status !== "created" && to.status !== "refunded"
              )
              .map((m) => {
                return (
                  <TicketOrderCard
                    key={m.id}
                    onClick={() => send({ type: "focus", data: m.id })}
                    onBlur={() => send({ type: "unfocus" })}
                    focus={focused === m.id}
                    ticketOrder={m}
                    ticketEvent={ticketEvent}
                    tickets={tickets}
                    onCheckInNext={() => {
                      const t = tickets.find((t) => !t.checkedInAt);
                      if (!t) return;

                      send({
                        type: "checkIn",
                        data: t.id,
                      });
                    }}
                    onCheckInAll={() => {
                      send({ type: "checkAllIn", data: m.id });
                    }}
                    loading={{
                      checkingIn:
                        saveTicketRefSnap.matches("fetching") &&
                        checkingInTicket
                          ? new Set([checkingInTicket])
                          : undefined,
                      fetchingTickets: fetchTicketsRefSnap.matches("fetching"),
                      checkingAllIn: saveTicketsRefSnap.matches("fetching"),
                    }}
                  />
                );
              })}
            {!fetchTicketOrdersLoading && !ticketOrders.length && (
              <NotFoundView
                empty={ticketEvent && ticketOrders.length === 0}
                onChooseEvent={openEventPicker}
                onCreateNewEvent={() => alert("Create new event event")}
              />
            )}
          </div>
        </PageContainer>
        <IonModal
          isOpen={pickingEvent}
          onDidDismiss={() => setPickingEvent(false)}
          mode="ios"
          breakpoints={[0, 0.9, 1]}
          initialBreakpoint={isPlatform("desktop") ? 1 : 0.9}
          canDismiss
          style={{
            "--border-radius": "44px",
            "--ion-background-color": "#0F1720",
          }}
        >
          <IonToolbar
            style={{
              "--ion-toolbar-background": "#0F1720",
            }}
          >
            <IonSegment
              value={filter}
              onIonChange={(ev) => {
                setFilter((ev.detail.value?.toString() as any) ?? "future");
              }}
              style={{
                "--ion-toolbar-segment-background-checked":
                  "var(--gm-color-bright-green-1)",
                "--ion-toolbar-segment-indicator-color":
                  "--ion-toolbar-segment-indicator-color",
              }}
            >
              <IonSegmentButton value="previous">
                <IonLabel>Tidligere</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value="future">
                <IonLabel>Fremtidige</IonLabel>
              </IonSegmentButton>
            </IonSegment>
            <IonBackButton />
          </IonToolbar>
          <IonContent>
            <PageContainer
              style={{
                padding: 20,
                gap: 10,
              }}
            >
              {!isFetchTicketEventsLoading &&
                fetchTicketEventsSnap.context.data?.map((te) => {
                  const now = new Date();
                  if (te.eventPeriod.type !== "once") return null;
                  const period = TimeOfDayPeriod.toDatePeriod(
                    te.eventPeriod.timePeriod,
                    new Date(te.eventPeriod.date)
                  );
                  return (
                    <TicketEventCard
                      title={te.title}
                      active={period.isWithin(now)}
                      date={new Date(te.eventPeriod.date)}
                      timePeriod={te.eventPeriod.timePeriod}
                      onClick={() => {
                        send({ type: "initialize", data: te.id });
                        setPickingEvent(false);
                      }}
                    />
                  );
                })}
              {isFetchTicketEventsLoading && <SpinnerView />}
            </PageContainer>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
}

TicketOverviewPage.TicketOrders = function ({
  ticketOrders,
  ticketEvent,
  tickets,
  focused,
  loading,
  onClick,
  onBlur,
}: {
  ticketEvent: TicketEvent;
  ticketOrders: Array<TicketOrder>;
  tickets: Array<Ticket>;
  focused?: TicketOrderId;
  loading: boolean;
  onClick?: (ticketOrderId: TicketOrderId) => void;
  onBlur?: () => void;
}) {
  return (
    <div className={css.Tickets}>
      {loading && <span>Henter...</span>}
      {ticketOrders?.map((m) => {
        return (
          <TicketOrderCard
            key={m.id}
            onClick={() => onClick?.(m.id)}
            onBlur={onBlur}
            focus={focused === m.id}
            ticketOrder={m}
            ticketEvent={ticketEvent}
            tickets={tickets}
          />
        );
      })}
    </div>
  );
};
